/**
 * Soft password gate for visitors and new wallets.
 *
 * IMPORTANT: this is obscurity, not security — the password lives in the client
 * bundle and the unlock flag is in localStorage. It only gates casual access and
 * must never protect sensitive data (real protection is Supabase RLS + on-chain
 * rules). The gate is fully DISABLED unless `VITE_GATE_PASSWORD` is set; there is
 * no default password.
 */
import { useState, FormEvent } from 'react';
import { useTheme } from '../context/ThemeContext';

const STORAGE_KEY = 'abc_gate_unlocked';
const EXPECTED_PASSWORD = (import.meta.env.VITE_GATE_PASSWORD ?? '').trim();

/** Whether a gate password is configured; when false the gate is bypassed. */
export function isGateEnabled(): boolean {
  return EXPECTED_PASSWORD.length > 0;
}

export function isGateUnlocked(): boolean {
  if (!isGateEnabled()) return true;
  return localStorage.getItem(STORAGE_KEY) === '1';
}

function setGateUnlocked(): void {
  localStorage.setItem(STORAGE_KEY, '1');
}

interface PasswordGateProps {
  onUnlock: () => void;
}

export function PasswordGate({ onUnlock }: PasswordGateProps) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const theme = useTheme();

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (password === EXPECTED_PASSWORD) {
      setGateUnlocked();
      onUnlock();
    } else {
      setError('Incorrect password');
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme.bg ?? theme.bgBase ?? '#faf5ff',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          width: '100%',
          maxWidth: '280px',
        }}
      >
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
          style={{
            padding: '0.75rem 1rem',
            fontSize: '1rem',
            backgroundColor: theme.inputBg ?? 'transparent',
            color: theme.inputColor ?? theme.textPrimary,
            border: `1px solid ${theme.inputBorder ?? theme.border}`,
            borderRadius: '0.5rem',
            outline: 'none',
          }}
        />
        <button
          type="submit"
          style={{
            padding: '0.75rem 1rem',
            background: theme.btnPrimaryBg ?? theme.primary,
            color: theme.btnPrimaryColor ?? 'white',
            border: 'none',
            borderRadius: '0.5rem',
            fontSize: '1rem',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Enter
        </button>
        {error && (
          <span style={{ color: '#dc2626', fontSize: '0.875rem' }}>{error}</span>
        )}
      </form>
    </div>
  );
}
